import { useMemo } from 'react'
import { NumericFormat, NumberFormatValues, SourceInfo } from 'react-number-format'

import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import CircularProgress from '@mui/material/CircularProgress'
import { CurrencyFieldProps } from './types'

export default function CurrencyFieldMui( {
	onRawChange,
	disabled,
	fullWidth,
	isInvalid,
	isLoading,
	value,
	intlNumberFormatOptions,
	defaultValue,
	type,
	min,
	id,
	onBlur,
	label,
	error,
	helperText,
}: CurrencyFieldProps ) {
	const onValueChange = ( values: NumberFormatValues, sourceInfo: SourceInfo ) => onRawChange( values.value, sourceInfo.source === 'event' )

	const formattedValue = useMemo( () => (
		value ? new Intl.NumberFormat( 'en-US', {
			style: 'decimal',
			currency: 'USD',
			maximumFractionDigits: 4,
			minimumFractionDigits: 2,
			...intlNumberFormatOptions
		} ).format( parseFloat( value ) ) : ''
	), [ value ] )

	return (
		<NumericFormat
			customInput={TextField}
			thousandSeparator
			valueIsNumericString
			decimalScale={4}
			fixedDecimalScale={false}
			prefix="$"
			onValueChange={onValueChange}
			disabled={disabled}
			fullWidth={fullWidth}
			// `isInvalid` (direct, bare usage) and `error` (injected by a wrapping
			// `FormRow`) are two paths to the same red-bordered state — a real
			// boolean OR, not a fallback, so `??` would be wrong: it would ignore
			// `error` whenever `isInvalid` is explicitly `false`.
			// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
			error={isInvalid || error}
			helperText={helperText}
			label={label}
			value={formattedValue}
			allowNegative={false}
			defaultValue={defaultValue as string | number | undefined}
			type={( type === 'text' || type === 'tel' || type === 'password' ) ? type as ( 'text' | 'tel' | 'password' ) : 'text'}
			id={id}
			onBlur={onBlur}
			slotProps={{
				htmlInput: { min },
				input: isLoading ? {
					endAdornment: <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>,
				} : undefined,
			}}
		/>
	)
}
